'use client';

import { c as _c } from "react/compiler-runtime";
import { AiSpendOverTimeCard } from '@/domains/pages/ai-spend/components/ai-spend-over-time-card';
import { SegmentSpendPerTaskCard } from '@/domains/pages/ai-spend/components/segment-spend-per-task-card';
import { SegmentSpendScaleCard } from '@/domains/pages/ai-spend/components/segment-spend-scale-card';
import { SegmentTotalSpendCard } from '@/domains/pages/ai-spend/components/segment-total-spend-card';
import { SpendByModelCard } from '@/domains/pages/ai-spend/components/spend-by-model-card';
import { SpendDistributionCard } from '@/domains/pages/ai-spend/components/spend-distribution-card';
import { AI_SPEND_KPI_GRID_CLASS, SEGMENT_SPEND_GROUP_OPTIONS, SPEND_RANGE_ORG_AVERAGE_LABEL, type SpendChartMode, type SpendGroupKind } from '@/domains/pages/ai-spend/constants';
import type { AiSpendDistributionData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-distribution-data';
import type { AiSpendTaskModelData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-task-model-data';
import type { AiSpendSegmentPageData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-segment-page-data';
import type { SegmentKind } from '@/domains/pages/shared/segment-kind';
import { segmentKindLabel } from '@/domains/pages/shared/segment-kind';
import type { TimeRangeConfig } from '@/api/generated/model';
import { cn } from '@/utils/shad';
type AiSpendSegmentViewProps = {
  pageKey: string;
  kind: SegmentKind;
  onSelectSegment: (name: string) => void;
  timeRange: TimeRangeConfig | undefined;
  data: AiSpendSegmentPageData;
  distribution: AiSpendDistributionData;
  taskModel: AiSpendTaskModelData;
  spendGroup: SpendGroupKind;
  onSpendGroupChange: (kind: SpendGroupKind) => void;
  spendChart: SpendChartMode;
  onSpendChartChange: (mode: SpendChartMode) => void;
};
export function AiSpendSegmentView(t0) {
  const $ = _c(58);
  const {
    kind,
    onSelectSegment,
    timeRange,
    data,
    distribution,
    taskModel,
    spendGroup,
    onSpendGroupChange,
    spendChart,
    onSpendChartChange
  } = t0;
  let t1;
  if ($[0] !== kind) {
    t1 = segmentKindLabel(kind);
    $[0] = kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const segmentNoun = t1;
  let t2;
  if ($[2] !== data.org.spendPerTask || $[3] !== data.segments || $[4] !== data.selectedSegmentName || $[5] !== kind || $[6] !== onSelectSegment) {
    t2 = <SegmentSpendScaleCard kind={kind} segments={data.segments} selectedName={data.selectedSegmentName} orgSpendPerTask={data.org.spendPerTask} onSelect={onSelectSegment} className="border-b border-b-lightborder" />;
    $[2] = data.org.spendPerTask;
    $[3] = data.segments;
    $[4] = data.selectedSegmentName;
    $[5] = kind;
    $[6] = onSelectSegment;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(AI_SPEND_KPI_GRID_CLASS, "items-stretch border-b border-b-lightborder");
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== data.selected || $[10] !== timeRange) {
    t4 = <SegmentTotalSpendCard selected={data.selected} timeRange={timeRange} className="grow-0 border-b border-b-lightborder" />;
    $[9] = data.selected;
    $[10] = timeRange;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== data.formatSegmentName || $[13] !== data.org || $[14] !== data.segments.length || $[15] !== data.selected || $[16] !== data.selectedSegmentName || $[17] !== data.spendPerTaskRange || $[18] !== data.spendPerTaskRank || $[19] !== kind) {
    t5 = <SegmentSpendPerTaskCard kind={kind} selected={data.selected} selectedName={data.selectedSegmentName} org={data.org} spendPerTaskRank={data.spendPerTaskRank} spendPerTaskRange={data.spendPerTaskRange} segmentCount={data.segments.length} formatSegmentName={data.formatSegmentName} className="grow-0" />;
    $[12] = data.formatSegmentName;
    $[13] = data.org;
    $[14] = data.segments.length;
    $[15] = data.selected;
    $[16] = data.selectedSegmentName;
    $[17] = data.spendPerTaskRange;
    $[18] = data.spendPerTaskRank;
    $[19] = kind;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t4 || $[22] !== t5) {
    t6 = <div className="flex min-w-0 flex-col border-r border-r-lightborder">{t4}{t5}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const t7 = data.labels;
  const t8 = data.totalSpend;
  const t9 = data.spendPerTask;
  const t10 = data.peerBand;
  let t11;
  if ($[24] !== data) {
    t11 = data.formatSegmentName(data.selectedSegmentName);
    $[24] = data;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== data.labels || $[27] !== data.orgSpendPerTaskOverTime || $[28] !== data.peerBand || $[29] !== data.spendPerTask || $[30] !== data.totalSpend || $[31] !== onSpendChartChange || $[32] !== segmentNoun || $[33] !== spendChart || $[34] !== t11) {
    t12 = <div className="flex h-full min-w-0"><AiSpendOverTimeCard mode={spendChart} onModeChange={onSpendChartChange} labels={t7} totalSpend={t8} spendPerTask={t9} peerBand={t10} seriesLabel={t11} avgSpendPerTask={data.orgSpendPerTaskOverTime} avgLineLabel={SPEND_RANGE_ORG_AVERAGE_LABEL} segmentNoun={segmentNoun} testId="ai-spend-segment-over-time-card" /></div>;
    $[26] = data.labels;
    $[27] = data.orgSpendPerTaskOverTime;
    $[28] = data.peerBand;
    $[29] = data.spendPerTask;
    $[30] = data.totalSpend;
    $[31] = onSpendChartChange;
    $[32] = segmentNoun;
    $[33] = spendChart;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12 || $[37] !== t6) {
    t13 = <div className={t3}>{t6}{t12}</div>;
    $[36] = t12;
    $[37] = t6;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== distribution.compositionSegments || $[40] !== distribution.isError || $[41] !== distribution.isLoading || $[42] !== distribution.refetch || $[43] !== distribution.rows || $[44] !== onSpendGroupChange || $[45] !== spendGroup) {
    t14 = <SpendDistributionCard kind={spendGroup} onKindChange={onSpendGroupChange} options={SEGMENT_SPEND_GROUP_OPTIONS} rows={distribution.rows} compositionSegments={distribution.compositionSegments} isLoading={distribution.isLoading} isError={distribution.isError} onRetry={distribution.refetch} />;
    $[39] = distribution.compositionSegments;
    $[40] = distribution.isError;
    $[41] = distribution.isLoading;
    $[42] = distribution.refetch;
    $[43] = distribution.rows;
    $[44] = onSpendGroupChange;
    $[45] = spendGroup;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== taskModel.isError || $[48] !== taskModel.isLoading || $[49] !== taskModel.refetch || $[50] !== taskModel.series || $[51] !== taskModel.taskLabels) {
    t15 = <SpendByModelCard taskLabels={taskModel.taskLabels} series={taskModel.series} isLoading={taskModel.isLoading} isError={taskModel.isError} onRetry={taskModel.refetch} />;
    $[47] = taskModel.isError;
    $[48] = taskModel.isLoading;
    $[49] = taskModel.refetch;
    $[50] = taskModel.series;
    $[51] = taskModel.taskLabels;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t13 || $[54] !== t14 || $[55] !== t15 || $[56] !== t2) {
    t16 = <>{t2}{t13}{t14}{t15}</>;
    $[53] = t13;
    $[54] = t14;
    $[55] = t15;
    $[56] = t2;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  return t16;
}